import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { formatDate } from 'date-fns';
import DOMPurify from 'dompurify';

export interface Dictionary<T> extends _.Dictionary<T> {
}

@Injectable({ providedIn: 'root' })
export class UtilitiesService {

    static sanitizeHtml(value: string): string {
      return DOMPurify.sanitize(value);
    }

    static cloneDeep(obj: any) {
      // Below approach does not properly clone Date objects.
      // JSON.parse(JSON.stringify(obj));
      const clonedObj = _.cloneDeep(obj);
      return  clonedObj;
    }

    static omitFunctions(obj: any) {
        return _.omit(obj, _.functions(obj));
    }

    static isEqual(obj1: any, obj2: any) {
        return _.isEqual(obj1, obj2);
    }

    static isValidDate(value: any): boolean {
    if (value instanceof Date) {
        return !isNaN(value.getTime());
    }
    if (typeof value === 'string') {
        const parsed = new Date(value);
        return !isNaN(parsed.getTime());
    }
    return false;
    }

    static formatDealDisplay(item: any): string {
      if (!item) return '';
      if (typeof item === 'string') return item;

      const dealName = (item.dealName ?? item.name ?? item.DealName ?? '').toString().trim();
      const dealNumber = (item.dealNumber ?? item.number ?? item.dealNo ?? item.DealNumber ?? '').toString().trim();
      const facility = (
        item.facilityId ??
        item.facilityNumber ??
        item.facility ??
        item.FacilityId ??
        item.FacilityNumber ??
        item.facilityDetail?.id ??
        item.facilityDetail?.name ??
        item.facilityDetail?.Id ??
        item.facilityDetail?.Name ??
        ''
      ).toString().trim();

      if (dealName && dealNumber && facility) {
        return `${dealName} (${dealNumber}) - ${facility}`;
      }
      if (dealName && dealNumber) {
        return `${dealName} (${dealNumber})`;
      }
      if (dealName) {
        return dealName;
      }
      if (dealNumber && facility) {
        return `${dealNumber} - ${facility}`;
      }
      return dealNumber || facility || '';
    }

    // Resolves a stored dealName/dealNumber value (often a raw deal number/ID) against a loaded deals list.
    static resolveDealName(value: unknown, deals: any[], isEu: boolean): string {
      const storedValue = String(value ?? '');
      const matchingDeal = (deals ?? []).find((deal: any) =>
        deal?.name === storedValue ||
        deal?.dealName === storedValue ||
        deal?.dealNumber === storedValue
      );

      if (isEu) {
        return matchingDeal?.name ?? matchingDeal?.dealName ?? storedValue;
      }

      return matchingDeal ? UtilitiesService.formatDealDisplay(matchingDeal) : storedValue;
    }

    // ERF facility display values are "{DealName} ({DealNumber}) - {LoanNumber}"; facilities under
    // the same deal only differ by the trailing loan number, so collapse those to the first occurrence
    // (comparing with the loan number suffix stripped, but keeping the original string for display).
    static dedupeFacilityDisplayValues(values: string[]): string[] {
      const seen = new Set<string>();
      const result: string[] = [];

      for (const value of values) {
        const dealPortion = (value ?? '').toString().replace(/\s*-\s*\d+\s*$/, '').trim();
        const dedupeKey = dealPortion || value;
        if (!seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          result.push(value);
        }
      }

      return result;
    }

   /* static isValidDateString(date: string) {
        if (!(typeof date === 'string') || date.length !== 10) {
            return false;
        }

        if (moment(date, 'MM/DD/YYYY').isValid()) {
            return true;
        }

        return this.isDate(this.convertStringToDate(date, 'MM/DD/YYYY'));
    }

    static isDate(date: Date) {
        return date instanceof Date && !isNaN(date.getTime());
    }

    static getValidDateFormat(dateString: string) {
        const formats = ['YYYY-MM-DD', 'MM-DD-YYYY', 'MM/DD/YYYY'];
        let dateFormat: string = null;
        if (dateString && dateString.length === 10) {
            formats.forEach(format => {
                if (moment(dateString, format).isValid()) {
                    dateFormat = format;
                }
            });
        }
        return dateFormat;
    }

    static isToday(date: Date) {
        const today = moment();
        return moment(today, 'MM-DD-YYYY').isSame(date, 'day');
    }

    static addDays(date: Date, days: number) {
        const newDate = moment(date).add(days, 'day');
        return newDate.toDate();
    }

    static subtractDays(date: Date, days: number) {
        const newDate = moment(date).subtract(days, 'day');
        return newDate.toDate();
    }

    static convertDateToString(date: Date, format: string) {
        return moment(date).format(format);
    }

    static convertStringToDate(date: string, format?: string) {
        if (format) {
            return moment(date, format).toDate();
        } else {
            format = this.getValidDateFormat(date);
            if (format) {
                return moment(date, format).toDate();
            }
        }
        return null;
    }
    static isSameDate(date1: Date, date2: Date) {
        return moment(date1).isSame(moment(date2), 'day');
    }

    // Return true if thisDate is before anotherDate
    static isBeforeDate(thisDate: Date, anotherDate: Date) {
        return moment(thisDate).isBefore(moment(anotherDate), 'day');
    }

    // Return true if thisDate is after anotherDate
    static isAfterDate(thisDate: Date, anotherDate: Date) {
        return moment(thisDate).isAfter(moment(anotherDate), 'day');
    }

    static isBeforeToday(date: Date) {
        return moment(date).isBefore(moment(), 'day');
    }

    static updateFromUTCToLocalTime(date: Date) {
        const now = new Date();
        const minuteOffset = now.getTimezoneOffset();
        date.setMinutes(date.getMinutes() + minuteOffset);
    }

    static sameListValues(list1: Array<any>, list2: Array<any>) {
        return _(list1).differenceWith(list2, _.isEqual).isEmpty();
    }

    static removeItemFromList(list: Array<any>, item: string, propertyName?: string) {
        let itemIndex = -1;

        if (!propertyName) {
            itemIndex = list.indexOf(item);
        } else {
            itemIndex = list.findIndex(listItem => {
                return listItem[propertyName] === item;
            });
        }
        list.splice(itemIndex, 1);
    }

    static fixTimeForDatePipeBug(value: Date) {
        // When time is 00:00:00, the date pipe thinks the date is yesterday
        // Changing it to 01:00:00 fixes the problem
        // https://github.com/angular/angular/issues/8319
        if (!value || !UtilitiesService.isDate(new Date(value))) {
            return '';
        }
        const fixedValue = new Date(value);
        if (value.getHours() === 0 && value.getMinutes() === 0 && value.getSeconds() === 0) {
           fixedValue.setHours(1, 0, 0);
        }
        return fixedValue;
    }

    // Capitalize the first letter of each word
    static convertToProperCase(s: string) {
        return _.startCase(s);
    }

    currentdate() {
        const today = moment();
        today.set({hour: 0, minute: 0, second: 0, millisecond: 0});
        return today.toDate();
    }
*/
     getFormattedDate(date: string | Date) {
        if (typeof date === 'string') {
            if (date.length >= 10) {
                date = date.substring(0, 10);
                return formatDate(date, 'MM-DD-YYYY');
            } else {
                return '';
            }
        }
        return formatDate( date, 'MM-DD-YYYY');
    }

}
